import './SeductionStageTracker.css';
import { DispatchCardDismiss } from './DispatchCardDismiss';

/**
 * V9.2.3 (2026-06-06) — SeductionStageTracker.
 *
 * Pure-presentation render of a `force_seduction_stage_advance` dispatch
 * action. Seduction is a 5-stage SEQUENCE per Ch 6 (canonical EFs
 * 10/9/8/5/4 per stage) — distinct from a one-shot Persuasion chart
 * lookup. This tracker shows progress through the 5 stages, surfacing
 * which stages are completed, which is the next attempt, and which is
 * the final consummation.
 *
 * Schema 1-to-1 with `force_seduction_stage_advance` tool args in
 * briefing_room_backend/routes/dispatch.py:476-545:
 *   - target_npc_id (string, required)
 *   - current_stage (integer 0-5, required): 0=not started, 1-4=mid,
 *     5=consummated
 *   - appearance_modifier_id: enum {plain, normal, good_looking,
 *     attractive, striking, sensational}
 *   - situational_modifier_ids: array of {candlelight, champagne, music,
 *     interrupted, hostile_environment}
 *   - rationale: GM voice-line ≤30 words (optional)
 *
 * Stage definitions per seed/tables/seduction_stages.json:
 *   1. The Look (EF 10)
 *   2. Opening Line (EF 9)
 *   3. Witty Conversation (EF 8)
 *   4. Beginning Intimacies (EF 5)
 *   5. When and Where? (EF 4)
 */

export type SeductionAppearanceId =
  | 'plain'
  | 'normal'
  | 'good_looking'
  | 'attractive'
  | 'striking'
  | 'sensational';

export type SeductionSituationalId =
  | 'candlelight'
  | 'champagne'
  | 'music'
  | 'interrupted'
  | 'hostile_environment';

export interface SeductionStageTrackerProps {
  targetNpcId: string;
  targetNpcDisplay?: string;
  currentStage: number;
  appearanceModifierId?: SeductionAppearanceId;
  situationalModifierIds?: SeductionSituationalId[];
  rationale?: string;
  /** V9.2.x.3: when provided, renders a ✕ dismiss button (top-right). */
  onDismiss?: () => void;
  testidPrefix?: string;
}

interface StageDef {
  stage: number;
  name: string;
  easeFactor: number;
}

const STAGES: readonly StageDef[] = [
  { stage: 1, name: 'The Look', easeFactor: 10 },
  { stage: 2, name: 'Opening Line', easeFactor: 9 },
  { stage: 3, name: 'Witty Conversation', easeFactor: 8 },
  { stage: 4, name: 'Beginning Intimacies', easeFactor: 5 },
  { stage: 5, name: 'When and Where?', easeFactor: 4 },
] as const;

const APPEARANCE_LABEL: Record<SeductionAppearanceId, string> = {
  plain: 'Plain',
  normal: 'Normal',
  good_looking: 'Good-looking',
  attractive: 'Attractive',
  striking: 'Striking',
  sensational: 'Sensational',
};

const SITUATIONAL_LABEL: Record<SeductionSituationalId, string> = {
  candlelight: 'Candlelight',
  champagne: 'Champagne',
  music: 'Music',
  interrupted: 'Interrupted',
  hostile_environment: 'Hostile environment',
};

const SITUATIONAL_VALENCE: Record<SeductionSituationalId, 'favorable' | 'unfavorable'> = {
  candlelight: 'favorable',
  champagne: 'favorable',
  music: 'favorable',
  interrupted: 'unfavorable',
  hostile_environment: 'unfavorable',
};

function defaultDisplayName(id: string): string {
  return id
    .split('_')
    .map((part) => part.length === 0 ? part : part[0].toUpperCase() + part.slice(1))
    .join(' ');
}

export function SeductionStageTracker({
  targetNpcId,
  targetNpcDisplay,
  currentStage,
  appearanceModifierId,
  situationalModifierIds,
  rationale,
  onDismiss,
  testidPrefix = 'br-seduction-tracker',
}: SeductionStageTrackerProps) {
  const display = targetNpcDisplay ?? defaultDisplayName(targetNpcId);
  const clampedStage = Math.max(0, Math.min(5, currentStage));
  const isConsummated = clampedStage === 5;
  const nextStage = isConsummated ? null : clampedStage + 1;
  const hasSituational =
    situationalModifierIds !== undefined && situationalModifierIds.length > 0;

  return (
    <aside
      className="br-seduction-tracker"
      data-testid={testidPrefix}
      data-target-npc-id={targetNpcId}
      data-current-stage={clampedStage}
      data-consummated={isConsummated ? 'true' : 'false'}
    >
      <DispatchCardDismiss onDismiss={onDismiss} testidPrefix={testidPrefix} />
      <header className="br-seduction-tracker-header">
        <span className="br-seduction-tracker-label">Seducing</span>
        <span
          className="br-seduction-tracker-target"
          data-testid={`${testidPrefix}-target`}
        >
          {display}
        </span>
      </header>

      <ol
        className="br-seduction-tracker-stages"
        data-testid={`${testidPrefix}-stages`}
      >
        {STAGES.map((s) => {
          const completed = s.stage <= clampedStage;
          const isNext = nextStage === s.stage;
          const state = completed ? 'completed' : isNext ? 'next' : 'upcoming';
          return (
            <li
              key={s.stage}
              className="br-seduction-tracker-stage"
              data-testid={`${testidPrefix}-stage-${s.stage}`}
              data-state={state}
            >
              <span className="br-seduction-tracker-stage-num br-label">
                {s.stage}
              </span>
              <span className="br-seduction-tracker-stage-name">{s.name}</span>
              <span className="br-seduction-tracker-stage-ef br-label">
                EF {s.easeFactor}
              </span>
            </li>
          );
        })}
      </ol>

      <p
        className="br-seduction-tracker-status"
        data-testid={`${testidPrefix}-status`}
      >
        {isConsummated
          ? 'Consummated — Reaction reroll at +5 modifier.'
          : clampedStage === 0
            ? 'Opening overture — next attempt is The Look (EF 10).'
            : `Next attempt: ${STAGES[nextStage! - 1].name} (EF ${STAGES[nextStage! - 1].easeFactor}).`}
      </p>

      {appearanceModifierId !== undefined && (
        <div
          className="br-seduction-tracker-appearance"
          data-testid={`${testidPrefix}-appearance`}
          data-appearance-id={appearanceModifierId}
        >
          <span className="br-seduction-tracker-appearance-label">Appearance:</span>
          <span className="br-seduction-tracker-appearance-value">
            {APPEARANCE_LABEL[appearanceModifierId]}
          </span>
        </div>
      )}

      {hasSituational && (
        <ul
          className="br-seduction-tracker-situational"
          data-testid={`${testidPrefix}-situational`}
        >
          {situationalModifierIds!.map((mod) => (
            <li
              key={mod}
              className="br-seduction-tracker-situational-chip"
              data-testid={`${testidPrefix}-situational-${mod}`}
              data-modifier-id={mod}
              data-valence={SITUATIONAL_VALENCE[mod]}
            >
              {SITUATIONAL_LABEL[mod]}
            </li>
          ))}
        </ul>
      )}

      {rationale !== undefined && rationale.length > 0 && (
        <p
          className="br-seduction-tracker-rationale"
          data-testid={`${testidPrefix}-rationale`}
        >
          {rationale}
        </p>
      )}
    </aside>
  );
}
